import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import FloatingInput from "../../../components/input/FloatingInput";
import CustomSelect from "../../../components/customselect/CustomSelect";
import api from "../../../api/axios";
import AddNewUserModel from "../popups/AddNewUserModel";
import ViewEditSalesRepModel from "../popups/ViewEditSalesRepModel";
import ResendWelcomeEmailModel from "../popups/ResendWelcomeEmailModel";
import ViewUsagesRepModel from "../popups/ViewUsagesRepModel";
import AssignRepModel from "../popups/AssignRepModel";
import Toast from '../../../components/common/Toast';
import { useModal } from "../../../context/ModalContext";
import { useAuth } from "../../../context/AuthContext";
import config from "../../../config/config";
import "./myadmin-list.css";

/** First/last/display for action labels — uses API fields when present, else splits `name`. */
function resolveUserIdentity(u) {
  const fn = typeof u?.first_name === "string" ? u.first_name.trim() : "";
  const ln = typeof u?.last_name === "string" ? u.last_name.trim() : "";
  if (fn || ln) {
    const displayName = [fn, ln].filter(Boolean).join(" ").trim() || (u?.name || "").trim() || "User";
    return { firstName: fn, lastName: ln, displayName };
  }
  const raw = (u?.name || "").trim();
  if (!raw) return { firstName: "", lastName: "", displayName: "User" };
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "", displayName: raw };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
    displayName: raw,
  };
}

function ActionsMenuIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="6" r="1.75" fill="currentColor" />
      <circle cx="12" cy="12" r="1.75" fill="currentColor" />
      <circle cx="12" cy="18" r="1.75" fill="currentColor" />
    </svg>
  );
}

export default function MyAdmin() {
  const { user: authUser } = useAuth();
  const isSalesManager = authUser?.user_type === "SM";
  const [searchInput, setSearchInput] = useState("");
  const { openModal, closeModal } = useModal();
  const [toast, setToast] = useState({ message: "", visible: false, type: 'success' });
  const { control, } = useForm({});

  const [searchQuery, setSearchQuery] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [userType, setUserType] = useState("");
  const userTypeOptions = [
    { value: "N", label: "User" },
    { value: "S", label: "Title Rep" }
  ];

  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10 });
  const [users, setUsers] = useState([]);
  const [togglingUserId, setTogglingUserId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ column: "name", direction: "asc" });

  /** Row actions menu: fixed position escapes overflow clipping when the table has no vertical scroll. */
  const [openActionsUserId, setOpenActionsUserId] = useState(null);
  const [actionsPanelBox, setActionsPanelBox] = useState(null);
  const actionsTriggerRefs = useRef({});
  const actionsCloseTimerRef = useRef(null);

  const clearActionsCloseTimer = () => {
    if (actionsCloseTimerRef.current) {
      clearTimeout(actionsCloseTimerRef.current);
      actionsCloseTimerRef.current = null;
    }
  };

  const placeActionsPanel = useCallback((userId) => {
    const el = actionsTriggerRefs.current[userId];
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const width = Math.min(320, window.innerWidth - 2 * margin);
    let left = r.left;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - width);
    }
    const estHeight = 360;
    let top = r.bottom + 6;
    if (top + estHeight > window.innerHeight - margin) {
      const above = r.top - estHeight - 6;
      top = above >= margin ? above : Math.max(margin, window.innerHeight - margin - estHeight);
    }
    setActionsPanelBox({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (openActionsUserId == null) {
      setActionsPanelBox(null);
      return;
    }
    placeActionsPanel(openActionsUserId);
  }, [openActionsUserId, placeActionsPanel, users.length]);

  useEffect(() => {
    if (openActionsUserId == null) return;
    const onReposition = () => placeActionsPanel(openActionsUserId);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [openActionsUserId, placeActionsPanel]);

  const handleActionsEnter = (userId) => {
    clearActionsCloseTimer();
    setOpenActionsUserId(userId);
  };

  const handleActionsLeave = () => {
    clearActionsCloseTimer();
    actionsCloseTimerRef.current = setTimeout(() => {
      setOpenActionsUserId(null);
      setActionsPanelBox(null);
    }, 200);
  };

  const closeActionsMenu = () => {
    clearActionsCloseTimer();
    setOpenActionsUserId(null);
    setActionsPanelBox(null);
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, page, pageSize, sortConfig, includeInactive, userType]);

  useEffect(() => {
    return () => clearActionsCloseTimer();
  }, []);

  useEffect(() => {
    if (openActionsUserId != null && !users.some((u) => u.id === openActionsUserId)) {
      setOpenActionsUserId(null);
      setActionsPanelBox(null);
    }
  }, [users, openActionsUserId]);

  // eslint-disable-next-line no-unused-vars -- used in commented AssignRepModel block
  const assignRep = async (uid, sid) => {
    await api.post("/users/assign-sales-rep/" + uid, { sales_rep_id: sid });
    handleUpdateToaster({ message: "Sales rep assigned successfully!", visible: true, type: 'success' });
    fetchUsers();
  }

  const handleUpdateToaster = ({ message, visible, type = 'success' }) => {
    setToast({ message, visible, type });
    setTimeout(() => setToast({ message: "", visible: false, type: 'success' }), 5000);
  };

  const loadUserList = () => {
    fetchUsers();
  };
  const fetchUsers = async () => {
    const params = {
      q: searchQuery || undefined,
      page,
      limit: pageSize,
      sortBy: sortConfig.column,
      sortOrder: sortConfig.direction,
      includeInactive: includeInactive ? 0 : 1,
      user_type: userType,
      exports: false,
    };

    const { data } = await api.get("/users/get-rep", { params });

    const list = data?.data ?? [];
    setUsers(list.map(u => ({
      id: u.id,
      name: u.name,
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      lastlogin: u.lastlogin ?? "",
      company_name: u.company_name ?? "",
      titleRepId: u.titleRepId,
      userPolicy: u.userPolicy ?? "",
      userTypeHtml: u.user_type,
      is_active: u.is_active !== undefined ? u.is_active : true,
    })));

    setMeta(data?.meta || {});
    if ((data?.data ?? []).length === 0) {
      setUsers([]);
      return;
    }

  };

  // eslint-disable-next-line no-unused-vars -- reserved for export feature
  const handleExport = async () => {
    const params = {
      q: searchQuery || undefined,
      page,
      limit: pageSize,
      sortBy: sortConfig.column,
      sortOrder: sortConfig.direction,
      includeInactive: includeInactive ? 0 : 1,
      user_type: userType,
      exports: true,
    };
    try {
      const respJson = await api.get("/users/get-rep", { params });
      const d = respJson?.data;
      if (d) {
        const url = (d.url) || (d.data && d.data.url) || (d?.data?.url);
        if (url) {
          window.open(url, '_blank');
          return;
        }
      }
    } catch (e) {
      // swallow
    }
  }

  const totalCount = meta.total ?? 0;
  const totalPages = meta.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize) || 1);
  const showingFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  function getPaginationRange(totalPagesArg, currentPage) {
    if (totalPagesArg <= 3) return [...Array(totalPagesArg)].map((_, i) => i + 1);
    if (currentPage === 1) return [1, 2, 3];
    if (currentPage === totalPagesArg) return [totalPagesArg - 2, totalPagesArg - 1, totalPagesArg];
    return [currentPage - 1, currentPage, currentPage + 1];
  }

  const sortThClass = (field) => {
    const base = "myadmin-th myadmin-th--sort";
    if (sortConfig.column !== field) return `${base} myadmin-th--sort-idle`;
    return sortConfig.direction === "asc"
      ? `${base} myadmin-th--sort-asc`
      : `${base} myadmin-th--sort-desc`;
  };

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { column, direction: "asc" };
    });
    setPage(1);
  };

  const handleToggleUserStatus = async (u) => {
    const nextActive = !u.is_active;
    const ok = window.confirm(
      nextActive
        ? "Are you sure you want to activate this user profile? They will be able to log in."
        : "Are you sure you want to deactivate this user profile? This will disable login immediately."
    );
    if (!ok) return;

    setTogglingUserId(u.id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${config.apiBaseUrl}/admin/users/${u.id}/toggle-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ is_active: nextActive }),
        }
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.message || `Request failed (${res.status})`);
      }
      handleUpdateToaster({
        message: nextActive ? "User activated successfully." : "User deactivated successfully.",
        visible: true,
        type: "success",
      });
      fetchUsers();
    } catch (err) {
      console.error("Toggle status error:", err);
      handleUpdateToaster({
        message: err?.message || "Could not update user status.",
        visible: true,
        type: "error",
      });
    } finally {
      setTogglingUserId(null);
    }
  };

  return (
    <div className="myadmin-root">
      <header className="myadmin-header">
        <div className="myadmin-header__titles">
          <h3 className="myadmin-title">User management</h3>
          <p className="myadmin-subtitle">Search, review, and manage users linked to your account.</p>
        </div>
        <button
          type="button"
          className="myadmin-btn myadmin-btn--primary"
          onClick={(e) => {
            e.preventDefault();
            openModal({
              title: "Add new user",
              size: "halffull",
              content: (
                <AddNewUserModel handleUpdateToaster={handleUpdateToaster} loadUserList={loadUserList} />
              )
            });
          }}
        >
          Add new user
        </button>
      </header>

      <section className="myadmin-filters" aria-label="Search and filters">
        <div className="myadmin-filters__grid">
          <div className="myadmin-filters__field">
            <span className="myadmin-filters__label">Type</span>
            <div className="form-group">
              <Controller
                name="user_type"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    options={userTypeOptions}
                    placeholder="Select Type"
                    value={field.value}
                    onChange={(val) => {
                      field.onChange(val);
                      setUserType(val);
                    }}
                  />
                )}
              />
            </div>
          </div>
          <div className="myadmin-filters__field">
            <span className="myadmin-filters__label">Name or email</span>
            <div className="form-group">
              <FloatingInput
                type="text"
                className="inpt-pla"
                placeholder="Enter name or email to search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setSearchQuery(searchInput);
                    setPage(1);
                  }
                }}
              />
            </div>
          </div>
          <div className="myadmin-filters__actions">
            <button
              type="button"
              className="myadmin-btn myadmin-btn--search"
              onClick={() => {
                setSearchQuery(searchInput);
                setPage(1);
              }}
            >
              Search
            </button>
            <button
              type="button"
              className="myadmin-btn myadmin-btn--ghost"
              onClick={() => {
                setSearchInput("");
                setSearchQuery("");
                setPage(1);
              }}
            >
              Reset
            </button>
          </div>
        </div>
        <div className="myadmin-filters__meta">
          <label className="myadmin-filters__check">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => {
                setIncludeInactive(e.target.checked);
                setPage(1);
              }}
            />
            <span>Include inactive users</span>
          </label>
          <span className="myadmin-count">
            Total count: <strong>{meta.total}</strong>
          </span>
        </div>
      </section>

      <div className="myadmin-table-card">
        <div className="myadmin-table-scroll">
          <table className="myadmin-table">
            <thead>
              <tr>
                <th className="myadmin-th myadmin-th--narrow" scope="col">Actions</th>
                <th className={sortThClass("name")} scope="col" onClick={() => handleSort("name")}>Name</th>
                <th className={sortThClass("email")} scope="col" onClick={() => handleSort("email")}>Email</th>
                <th className={sortThClass("company_name")} scope="col" onClick={() => handleSort("company_name")}>Company Name</th>
                <th className={sortThClass("lastlogin")} scope="col" onClick={() => handleSort("lastlogin")}>Last Logged in</th>
                <th className={sortThClass("titlerep")} scope="col" onClick={() => handleSort("titlerep")}>{userType === 'N' ? 'Sales Rep' : 'Title'}</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td className="myadmin-empty" colSpan={6}>
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const who = resolveUserIdentity(user);
                  return (
                    <tr key={user.id}>
                      <td className="myadmin-td myadmin-td--actions">
                        <div
                          className="myadmin-act"
                          onMouseEnter={() => handleActionsEnter(user.id)}
                          onMouseLeave={handleActionsLeave}
                        >
                          <button
                            type="button"
                            className="myadmin-act__trigger"
                            title={`Open actions for ${who.displayName}`}
                            aria-label={`Open actions menu for ${who.displayName}`}
                            aria-expanded={openActionsUserId === user.id}
                            aria-haspopup="menu"
                            ref={(el) => {
                              if (el) actionsTriggerRefs.current[user.id] = el;
                              else delete actionsTriggerRefs.current[user.id];
                            }}
                          >
                            <ActionsMenuIcon />
                          </button>
                          <div
                            className={`myadmin-act__panel ${openActionsUserId === user.id && actionsPanelBox ? "myadmin-act__panel--open" : ""}`}
                            role="menu"
                            style={
                              openActionsUserId === user.id && actionsPanelBox
                                ? {
                                  position: "absolute",
                                  bottom: 0,
                                  left: '100%',
                                  width: actionsPanelBox.width,
                                  zIndex: 10050,
                                }
                                : { display: "none" }
                            }
                          >
                            <div className="myadmin-act__identity">
                              <span className="myadmin-act__identity-label">Actions for</span>
                              <span className="myadmin-act__identity-name">{who.displayName}</span>
                            </div>
                            <button
                              type="button"
                              className="myadmin-act__item"
                              role="menuitem"
                              title={`View — ${who.displayName}`}
                              onClick={(e) => {
                                e.preventDefault();
                                closeActionsMenu();
                                openModal({
                                  title: `${user.userPolicy === 'S' ? 'View Sales Rep' : 'View User'}`,
                                  size: "halffull",
                                  content: (
                                    <ViewEditSalesRepModel
                                      user={user}
                                      handleUpdateToaster={handleUpdateToaster}
                                      loadUserList={loadUserList}
                                      closeModal={closeModal}
                                      onSelect={(rep) => {
                                        console.log("Selected rep:", rep);
                                      }}
                                    />
                                  )
                                });
                              }}
                            >
                              View
                            </button>                           

                            {isSalesManager && user.userPolicy === 'N' && (
                              <button
                                type="button"
                                className="myadmin-act__item"
                                role="menuitem"
                                disabled={togglingUserId === user.id}
                                title={
                                  togglingUserId === user.id
                                    ? undefined
                                    : user.is_active
                                      ? `Disable user — ${who.displayName}`
                                      : `Enable user — ${who.displayName}`
                                }
                                onClick={(e) => {
                                  e.preventDefault();
                                  closeActionsMenu();
                                  handleToggleUserStatus(user);
                                }}
                              >
                                {togglingUserId === user.id
                                  ? "…"
                                  : user.is_active
                                    ? "Disable user"
                                    : "Enable user"}
                              </button>
                            )}
                            {user.userPolicy === "N" && (
                              <>
                                <button
                                  type="button"
                                  className="myadmin-act__item"
                                  role="menuitem"
                                  title={`Resend welcome email — ${who.displayName}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    closeActionsMenu();
                                    openModal({
                                      user: user,
                                      title: "Resend welcome email",
                                      size: "smallmodel",
                                      content: (
                                        <ResendWelcomeEmailModel
                                          user={user}
                                          handleUpdateToaster={handleUpdateToaster}
                                          onSelect={(rep) => {
                                            console.log("Selected rep:", rep);
                                          }}
                                        />
                                      )
                                    });
                                  }}
                                >
                                  Resend welcome email
                                </button>

                                <button
                                  type="button"
                                  className="myadmin-act__item"
                                  role="menuitem"
                                  title={`Associate to rep — ${who.displayName}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    closeActionsMenu();
                                    openModal({
                                      title: "Associate to Rep",
                                      size: "halffull",
                                      content: (
                                        <AssignRepModel
                                          user={user}
                                          closeModal={closeModal}
                                          onSelect={(rep) => {
                                            console.log("Selected rep:", rep);
                                            assignRep(user.id, rep.titleRepId);
                                          }}
                                        />
                                      )
                                    });
                                  }}
                                >
                                  Associate to rep
                                </button>

                                 {user.userPolicy === 'N' && (
                              <button
                                type="button"
                                className="myadmin-act__item"
                                role="menuitem"
                                title={`View usage — ${who.displayName}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  closeActionsMenu();
                                  openModal({
                                    title: "View usage",
                                    size: "heightparcel",
                                    content: (
                                      <ViewUsagesRepModel
                                        user={user}
                                        closeModal={closeModal}
                                      />
                                    )
                                  });
                                }}
                              >
                                View usage
                              </button>
                            )}
                            
                              </>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="myadmin-td">
                        <div className="myadmin-name-cell">
                          <span
                            className={
                              user.is_active
                                ? "myadmin-status-dot myadmin-status-dot--active"
                                : "myadmin-status-dot myadmin-status-dot--inactive"
                            }
                            title={user.is_active ? "Active user" : "Inactive user"}
                            role="img"
                            aria-label={user.is_active ? "Active" : "Inactive"}
                          />
                          <span className="myadmin-name-text">{user.name}</span>
                        </div>
                      </td>
                      <td className="myadmin-td myadmin-html-cell"><div className="tab-wrapper" dangerouslySetInnerHTML={{ __html: user.email }} /></td>
                      <td className="myadmin-td myadmin-td--muted">{user.company_name || "—"}</td>
                      <td className="myadmin-td myadmin-td--muted">{user.lastlogin || "—"}</td>
                      <td className="myadmin-td myadmin-html-cell">
                        <div className="tab-wrapper" dangerouslySetInnerHTML={{ __html: user.userTypeHtml }} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="myadmin-toolbar">
        <span className="myadmin-toolbar__range">
          {totalCount > 0
            ? `Showing ${showingFrom}–${showingTo} of ${totalCount}`
            : ""}
        </span>

        <div className="myadmin-toolbar__pagesize">
          <label htmlFor="myadmin-page-size">Rows per page</label>
          <select
            id="myadmin-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        {totalPages > 1 && (
          <nav className="myadmin-pager" aria-label="Pagination">
            <button
              type="button"
              className="myadmin-pager__btn"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </button>
            {getPaginationRange(totalPages, page).map((p) => (
              <button
                type="button"
                key={p}
                className={`myadmin-pager__btn ${page === p ? "myadmin-pager__btn--active" : ""}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              className="myadmin-pager__btn"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </nav>
        )}
      </div>

      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
    </div>
  );
}
